import React, { useRef, useState } from "react";
import { Editor as TinyMCEEditor } from "tinymce";
import { Editor, IAllProps } from "@tinymce/tinymce-react";
import { useAxios } from "myAxios";
import { useRouter } from "next/router";

export interface useTinyMCEProps extends IAllProps {
  customPlugins?: Array<((editor: TinyMCEEditor) => void) | undefined>;
  onEditorChange?: (content: string) => void;
}

/**
 * @desc TinyMCE readonly not working, add workaround to make it work
 */
function useTinyMCE(props?: useTinyMCEProps) {
  const editorRef = useRef<null | TinyMCEEditor>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [editorContent, setEditorContent] = useState("");
  const submitAxios = useAxios(undefined, "dataService", false);
  const router = useRouter();
  const { customPlugins, onEditorChange, ...rest } = props ?? {};
  // Controlled editors use the React integration for regular content changes.
  const isValueControlled = rest.value !== undefined;
  const onEditorChangeRef = useRef(onEditorChange);
  onEditorChangeRef.current = onEditorChange;
  const editor = (
    <Editor
      {...rest}
      onEditorChange={isValueControlled ? onEditorChange : undefined}
      tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.3/tinymce.min.js"
      disabled={rest?.init?.readonly}
      onInit={(_e, editor) => {
        editorRef.current = editor;
        if (rest?.init?.readonly) {
          editor.getBody().setAttribute("contenteditable", "false");
        }
        editor.on("FullscreenStateChanged", (e) => {
          setIsFullScreen(e?.state ?? false);
        });
      }}
      init={{
        height: 500,
        menubar: false,
        paste_remove_styles_if_webkit: router?.pathname?.includes("expand-plan") ? true : false,
        plugins: [
          "advlist",
          "autolink",
          "lists",
          "link",
          "fullscreen",
          "help",
          "quickbars",
          "emoticons",
          "table",
          "code",
        ],
        setup: (editor) => {
          customPlugins?.forEach((x) => x?.(editor));
          // add default font family
          editor.on("init", function (ed) {
            ed.target.editorCommands.execCommand("fontName", false, "Arial");
          });
          // for control send email page send together btn
          editor.on("input NodeChange", function () {
            setEditorContent(editor.getContent());
          });
          if (isValueControlled) {
            let pasteAsPlainText = false;

            editor.on("keydown", function (event) {
              pasteAsPlainText =
                (event.ctrlKey || event.metaKey) &&
                event.shiftKey &&
                (event.key?.toLowerCase() === "v" || event.keyCode === 86);
            });

            editor.on("keyup", function (event) {
              if (event.key?.toLowerCase() === "v" || event.keyCode === 86) {
                pasteAsPlainText = false;
              }
            });

            editor.on(
              "paste",
              function (event) {
                const clipboardData = event.clipboardData;
                const plainText = clipboardData?.getData("text/plain") ?? "";
                const html = clipboardData?.getData("text/html") ?? "";
                const uriList = clipboardData?.getData("text/uri-list") ?? "";
                const shouldHandlePlainText =
                  plainText.length > 0 && (pasteAsPlainText || (!html && !uriList));

                pasteAsPlainText = false;
                if (!shouldHandlePlainText) return;

                event.preventDefault();
                editor.execCommand("mceInsertClipboardContent", false, {
                  text: plainText,
                });
                onEditorChangeRef.current?.(editor.getContent());
              },
              true
            );
          } else {
            // Avoid treating programmatic SetContent as user edits.
            editor.on("input change undo redo", function () {
              onEditorChangeRef.current?.(editor.getContent());
            });
          }
          editor.on("change", function () {
            const content = editor.getContent();
            const regex = /^(<p>&nbsp;<\/p>)+/;
            if (regex.test(content)) editor.setContent(content.replace(regex, "").trim());
          });
        },
        font_formats: "Arial=arial,sans-serif;",
        font_size_formats: "8px 10px 12px 14px 16px 18px 24px 36px 48px",
        quickbars_selection_toolbar: false,
        toolbar: rest?.init?.readonly
          ? false
          : `undo redo | bold italic underline strikethrough forecolor removeformat | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | quickimage link table  emoticons fullscreen | help | code`,
        content_style: `body { font-family:Arial,sans-serif; font-size:14px; }
          .mce-mention {color: #0065ff;text-decoration: underline; }
          .mce-item-table:not([border]), .mce-item-table:not([border]) caption, .mce-item-table:not([border]) td, .mce-item-table:not([border]) th, .mce-item-table[border="0"], .mce-item-table[border="0"] caption, .mce-item-table[border="0"] td, .mce-item-table[border="0"] th, table[style*="border-width: 0px"], table[style*="border-width: 0px"] caption, table[style*="border-width: 0px"] td, table[style*="border-width: 0px"] th {border: none;}
          `,
        contextmenu: false,
        quickbars_insert_toolbar: false,
        browser_spellcheck: true,
        statusbar: false,
        relative_urls: false,
        help_tabs: ["shortcuts"],
        newline_behavior: "linebreak",
        paste_webkit_styles:
          "font-size font-family font-weight font-style text-decoration color background-color line-height",
        paste_postprocess: (editor, args) => {
          // remove start/end blank
          args.node.innerHTML = args.node.innerHTML.replace(/^\s*\n/gm, "").replace(/\n\s*$/gm, "");
          // remove middle muti repeat blank
          args.node.innerHTML = args.node.innerHTML.replace(/\n{2,}/g, "\n");
        },
        images_upload_handler: (blobInfo, progress) =>
          new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append("file", blobInfo.blob(), blobInfo.filename());
            submitAxios
              .post<string>(`/dataservice/v1/files`, formData, {
                onUploadProgress: function (e) {
                  progress((e.loaded / e.total) * 100);
                },
              })
              .then(({ data }) => {
                resolve(`/api/dataservice-blob/v1/files/${data}?cache=1`);
              })
              .catch((e) => {
                reject(e);
              });
          }),
        ...rest?.init,
      }}
    />
  );
  return { editor, editorRef, isFullScreen, editorContent };
}

export default useTinyMCE;
